import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ApplicationRef, APP_INITIALIZER } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import localePtBr from '@angular/common/locales/pt';
import localeEs from '@angular/common/locales/es';
import { registerLocaleData } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ngfModule } from 'angular-file';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { EnvelopeModule } from './envelope/envelope.module';
import { NewEnvelopeComponent } from './envelope/new-envelope/new-envelope.component';
import { MainEnvelopeComponent } from './main-envelope/main-envelope.component';

import { NgxMaskModule } from 'ngx-mask';

import { CookieService } from 'ngx-cookie-service';
import { RedirectSuiteComponent } from './pages/redirect-suite/redirect-suite.component';

import { SharedModule } from './modules/shared/shared.module';

import { GoogleAnalyticsModule } from './modules/google-analytics/google-analytics.module';
import { environment } from 'src/environments/environment';
import { AuthService } from './services/auth.service';
import { ProfileValidateTeams } from './envelope/services/profileValidate.service';
import { CustomTranslateLoader } from './translate/http/custom-translate-loader';
import { ApiUrlUtil } from './util/api-url-util';
import '@angular/localize/init';
import { appInitializer } from './services/app.initializer';
import { CookieWrapperService } from './services/cookie-wrapper.service';
import { ButtonBaseboardComponent } from './shared-components/button-baseboard/button-baseboard.component';
import { EnvelopeSidebarDetailComponent } from './envelope/envelope-sidebar-detail/envelope-sidebar-detail.component';
import { EnvelopeSidebarCustomFieldsComponent } from './envelope/envelope-sidebar-custom-fields/envelope-sidebar-custom-fields.component';
import { EnvelopeSidebarEditCustomFieldComponent } from './envelope/envelope-sidebar-edit-custom-field/envelope-sidebar-edit-custom-field.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonAddEnvelopeComponent } from './shared-components/button-add-envelope/button-add-envelope.component';
import { TenantCustomizationService } from './services/tenant-customization.service';
import { ExecutionEnvironment } from './enum/execution-environment.enum';

declare let gtag: Function;

@NgModule({
  declarations: [
    AppComponent,
    NewEnvelopeComponent,
    MainEnvelopeComponent,
    RedirectSuiteComponent,
    ButtonBaseboardComponent,
    EnvelopeSidebarDetailComponent,
    EnvelopeSidebarCustomFieldsComponent,
    EnvelopeSidebarEditCustomFieldComponent,
    ButtonAddEnvelopeComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    NgbModule,
    EnvelopeModule,
    ngfModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient],
      },
    }),
    NgxMaskModule.forRoot(),
    GoogleAnalyticsModule.forRoot(),
    SharedModule,
    BrowserAnimationsModule
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [AuthService, TenantCustomizationService] },
    CookieService,
    ProfileValidateTeams
  ]
})
export class AppModule {
  constructor(
    private translateService: TranslateService,
    private cookieWrapperService: CookieWrapperService,
    private authService: AuthService
  ) { }

  configureTranslate(): void {
    registerLocaleData(localePtBr);
    registerLocaleData(localeEs);
    this.translateService.addLangs(['en', 'pt', 'es']);
    this.translateService.setDefaultLang('en');
  }

  googleAnalytics() {

    var env = ApiUrlUtil.getExecutionEnvironment();
    var domain = 'auto';

    if (env === ExecutionEnvironment.development)
      domain = '.dev.satelitti.com.br'
    else if (env === ExecutionEnvironment.homologation)
      domain = '.hom.satelitti.com.br'
    else if (env === ExecutionEnvironment.production)
      domain = '.satelitti.com.br'

    gtag('config', ApiUrlUtil.googleAnalytics4Code(), {
      cookie_domain: domain,
      cookie_flags: 'SameSite=None;Secure'
    });
  }

  ngDoBootstrap(appRef: ApplicationRef): void {
    this.configureTranslate();
    var suiteLanguage = this.cookieWrapperService.getSuiteLanguage();
    let lang = 'en';

    if (suiteLanguage && suiteLanguage.language) {
      lang = suiteLanguage.language;
    } else {
      const browserLang = this.translateService.getBrowserLang();
      lang = browserLang.match(/en|pt|es/) ? browserLang : 'en';
    }

    const startApp = () => {
      if (environment.googleAnalytics.enabled) {
        this.googleAnalytics();
      }
      appRef.bootstrap(AppComponent);
    };

    this.translateService.use(lang).subscribe({
      next: startApp,
      error: startApp
    });
  }
}